import React from 'react';
import { Box, Typography, Paper, IconButton, Tooltip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TimelineView from './views/TimelineView';
import FavoritesView from './views/FavoritesView';
import KeywordView from './views/KeywordView';
import DiscoveryView from './views/DiscoveryView';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import { useData } from '../context/DataContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';

const MainContent = ({ 
  currentView, 
  conversations, 
  themes,
  tags,
  favorites,
  selectedConversation,
  onConversationClick,
  searchQuery,
  filters,
  onFilterChange,
  onToggleFavorite,
  onSearch,
  onNavItemClick
}) => {
  const theme = useTheme();
  // データコンテキストから閲覧履歴と記録関数を取得
  const { recordView } = useData();
  const listContainerRef = React.useRef(null);
  const [scrollPosition, setScrollPosition] = React.useState(0);
  
  // 会話がクリックされたときに閲覧履歴を記録する関数
  const handleConversationClick = (conversation) => {
    // 詳細ビューに移動する前に、現在のスクロール位置を保存
    if (conversation && listContainerRef.current) {
      setScrollPosition(listContainerRef.current.scrollTop);
      recordView(conversation.id);
    }
    onConversationClick(conversation);
  };

  // タイムラインビューに戻ったときにスクロール位置を復元
  React.useEffect(() => {
    if (!selectedConversation && listContainerRef.current) {
      // DOMの更新が完了した後にスクロール位置を設定
      const timer = setTimeout(() => {
        if (listContainerRef.current) {
          listContainerRef.current.scrollTop = scrollPosition;
        }
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [selectedConversation, scrollPosition]);



  // 選択された会話がある場合はコンテキスト全体を表示
  if (selectedConversation) {
    return (
      <Box 
        component={Paper} 
        sx={{ 
          flexGrow: 1, 
          p: 3, 
          overflow: 'auto',
          m: 1,
          borderRadius: 1,
          position: 'relative'
        }}
      >
        {/* 操作ボタン */}
        <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 100, display: 'flex', gap: 1 }}>
          {/* 戻るボタン */}
          <IconButton 
            onClick={() => onConversationClick(null)}
            color="primary"
            aria-label="時系列ビューに戻る"
            sx={{
              bgcolor: 'white',
              boxShadow: 2,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          {/* お気に入りボタン */}
          <Tooltip title={favorites && favorites.includes(selectedConversation.id) ? "お気に入りから削除" : "お気に入りに追加"}>
            <IconButton
              onClick={() => onToggleFavorite && onToggleFavorite(selectedConversation.id)}
              color={favorites && favorites.includes(selectedConversation.id) ? "warning" : "default"}
              aria-label={favorites && favorites.includes(selectedConversation.id) ? "お気に入りから削除" : "お気に入りに追加"}
              sx={{
                bgcolor: 'white',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                }
              }}
            >
              {favorites && favorites.includes(selectedConversation.id) ? <StarIcon /> : <StarOutlineIcon />}
            </IconButton>
          </Tooltip>
        </Box>
        
        <Box sx={{ mt: 5 }}>  {/* ボタンの下にコンテンツを配置 */}
          <Box sx={{ 
            '& p': { margin: '0.8em 0' },
            '& pre': { 
              backgroundColor: alpha(theme.palette.text.primary, 0.05),
              padding: '12px',
              borderRadius: '8px',
              overflow: 'auto',
              border: `1px solid ${theme.palette.divider}`
            },
            '& code': {
              backgroundColor: alpha(theme.palette.text.primary, 0.1),
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.9em',
              fontFamily: 'monospace'
            },
            '& blockquote': {
              borderLeft: `4px solid ${theme.palette.divider}`,
              paddingLeft: '16px',
              margin: '16px 0',
              fontStyle: 'italic',
              color: 'text.secondary'
            },
            '& ul, & ol': { 
              paddingLeft: '24px',
              margin: '8px 0'
            },
            '& li': {
              margin: '4px 0'
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': { 
              margin: '1.2em 0 0.6em 0',
              fontWeight: 'bold',
              lineHeight: 1.3
            },
            '& h1': { fontSize: '2em', borderBottom: `2px solid ${theme.palette.divider}`, paddingBottom: '8px' },
            '& h2': { fontSize: '1.5em', borderBottom: `1px solid ${theme.palette.divider}`, paddingBottom: '4px' },
            '& h3': { fontSize: '1.3em' },
            '& table': {
              border: `1px solid ${theme.palette.divider}`,
              borderCollapse: 'collapse',
              margin: '16px 0',
              width: '100%'
            },
            '& th, & td': {
              border: `1px solid ${theme.palette.divider}`,
              padding: '8px 12px',
              textAlign: 'left'
            },
            '& th': {
              backgroundColor: theme.palette.action.hover,
              fontWeight: 'bold'
            },
            '& hr': {
              border: 'none',
              borderTop: `2px solid ${theme.palette.divider}`,
              margin: '24px 0'
            }
          }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight]}
            >
              {selectedConversation.content || ''}
            </ReactMarkdown>
          </Box>
          
          {selectedConversation.imageData && (
            <Box sx={{ mt: 2 }}>
              <img 
                src={selectedConversation.imageData} 
                alt="コンテキスト画像" 
                style={{ maxWidth: '100%', maxHeight: '300px' }} 
              />
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // 現在のビューに応じたコンポーネントを表示
  const renderCurrentView = () => {
    switch (currentView) {
      case 'timeline':
        return (
          <TimelineView 
            conversations={conversations} 
            onConversationClick={handleConversationClick}
            searchQuery={searchQuery}
            filters={filters}
            onFilterChange={onFilterChange}
            favorites={favorites}
            onToggleFavorite={onToggleFavorite}
            onSearch={onSearch}
          />
        );
      case 'favorites':
        return (
          <FavoritesView 
            conversations={conversations} 
            onConversationClick={handleConversationClick}
            searchQuery={searchQuery}
            favorites={favorites}
            onToggleFavorite={onToggleFavorite}
            onSearch={onSearch}
          />
        );
      case 'keywords':
        return <KeywordView conversations={conversations} onConversationClick={handleConversationClick} />;
      case 'discovery':
        return <DiscoveryView conversations={conversations} onConversationClick={handleConversationClick} onNavItemClick={onNavItemClick} />;
      default:
        return (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5">ビューが選択されていません</Typography>
          </Box>
        );
    }
  };

  return (
    <Box 
      ref={listContainerRef}
      component={Paper} 
      sx={{ 
        flexGrow: 1, 
        p: 2, 
        overflow: 'auto',
        m: 1,
        borderRadius: 1,
      }}
    >
      {renderCurrentView()}
    </Box>
  );
};

export default MainContent;
